import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  useGetIndividualQuery,
  useRequestVerificationMutation,
} from "../../../../store/Company/companyApiSlice";
import { CommonToast } from "../../../common/Toast";
/* The TIP-505 ladder, not the legacy chain: only this one can answer "member",
   and "member" is the whole difference between Get Verified and Get
   Membership. profileStatus() never returns it. */
import { statusLadder } from "./profileStatus";
import {
  isClaimedCompanyForViewer,
  isOwnerRole,
  ownerCtaKind,
  verificationRequestStatus,
} from "./ownerCta";

/* The owner's CTA, wired.
 *
 * TRANSCRIBED from BannerCompanyCard: the same `requestVerification` mutation
 * (TIP-491/507), the same success and failure toasts, the same
 * `verifyRequested` latch, and the same `/membership/:companyId` route for Get
 * Membership.
 */
export const useOwnerCta = ({ company, role }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userInfo, token } = useSelector((state) => state.auth);
  const individualID = userInfo?.individual?.[0]?.individualID;

  const { data: individualData } = useGetIndividualQuery(
    { individualID, token },
    { skip: !individualID }
  );

  const [requestVerification, { isLoading: submitting }] =
    useRequestVerificationMutation();
  const [verifyRequested, setVerifyRequested] = useState(false);

  /* The live page reads this off the URL, not off the company payload — a
     WorldBox record has no companyID, and this branch only ever applies to a
     TREVEX one anyway. */
  const currentCompanyId = id;

  const getVerified = async () => {
    try {
      await requestVerification({ companyID: currentCompanyId, token }).unwrap();
      setVerifyRequested(true);
      CommonToast.success(
        "Verification requested — our team will review it shortly."
      );
    } catch (err) {
      CommonToast.error(
        err?.data?.detail || "Couldn't request verification. Please try again."
      );
    }
  };

  const getMembership = () => {
    if (!currentCompanyId) return;
    window.scrollTo(0, 0);
    navigate(`/membership/${currentCompanyId}`);
  };

  /* Transcribed: this whole branch only applies when the viewer is linked to
     the company and that link is claimed. */
  const isOwnCompany = isClaimedCompanyForViewer(userInfo, currentCompanyId);

  const kind = ownerCtaKind({
    verifiedCompany: Boolean(company?.verifiedCompany),
    requestStatus: verificationRequestStatus(individualData, currentCompanyId),
    role,
    profileStatus: statusLadder(company),
    verifyRequested,
    isOwner: isOwnerRole(role),
  });

  return {
    isOwnCompany,
    kind: isOwnCompany ? kind : null,
    submitting,
    getVerified,
    getMembership,
  };
};

export default useOwnerCta;
